import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  Stack,
  Grid,
  Chip,
  IconButton,
  Button,
  Paper,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DevicesIcon from '@mui/icons-material/Devices';
import DescriptionIcon from '@mui/icons-material/Description';
import DiamondIcon from '@mui/icons-material/Diamond';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CategoryIcon from '@mui/icons-material/Category';

const ITEM_CATEGORIES = [
  { label: 'Medicines', value: 'medicine', icon: <LocalHospitalIcon />, color: '#f44336' },
  { label: 'Food & Beverages', value: 'food', icon: <RestaurantIcon />, color: '#4caf50' },
  { label: 'Clothing', value: 'clothing', icon: <CheckroomIcon />, color: '#2196f3' },
  { label: 'Electronics', value: 'electronic', icon: <DevicesIcon />, color: '#ff9800' },
  { label: 'Documents', value: 'document', icon: <DescriptionIcon />, color: '#9c27b0' },
  { label: 'Valuables', value: 'valuable', icon: <DiamondIcon />, color: '#ffeb3b' },
  { label: 'Medical Equipment', value: 'medical_equipment', icon: <MedicalServicesIcon />, color: '#00bcd4' },
  { label: 'Other', value: 'other', icon: <CategoryIcon />, color: '#795548' }
];

const COMMON_ITEMS = [
  // Medicines
  { name: 'Prescription Medicines', category: 'medicine', returnable: false },
  { name: 'Tablets', category: 'medicine', returnable: false },
  { name: 'Syrup/Medicine Bottle', category: 'medicine', returnable: true },
  { name: 'IV Fluids', category: 'medicine', returnable: true },
  { name: 'Injections', category: 'medicine', returnable: false },
  { name: 'Dressings/Bandages', category: 'medicine', returnable: false },
  { name: 'Oxygen Cylinder', category: 'medical_equipment', returnable: true },
  { name: 'Nebulizer Kit', category: 'medical_equipment', returnable: true },
  
  // Food
  { name: 'Home Cooked Food', category: 'food', returnable: true },
  { name: 'Fruits', category: 'food', returnable: false },
  { name: 'Milk/Milk Products', category: 'food', returnable: true },
  { name: 'Juice', category: 'food', returnable: false },
  { name: 'Water Bottles', category: 'food', returnable: true },
  { name: 'Snacks', category: 'food', returnable: false },
  
  // Clothing
  { name: 'Clothes', category: 'clothing', returnable: true },
  { name: 'Footwear', category: 'clothing', returnable: true },
  { name: 'Blanket', category: 'clothing', returnable: true },
  { name: 'Pillow', category: 'clothing', returnable: true },
  { name: 'Towel', category: 'clothing', returnable: true },
  
  // Electronics
  { name: 'Mobile Phone', category: 'electronic', returnable: true },
  { name: 'Charger', category: 'electronic', returnable: true },
  { name: 'Laptop', category: 'electronic', returnable: true },
  { name: 'Tablet', category: 'electronic', returnable: true },
  { name: 'Power Bank', category: 'electronic', returnable: true },
  { name: 'Headphones', category: 'electronic', returnable: true },
  
  // Documents
  { name: 'Medical Reports', category: 'document', returnable: true },
  { name: 'X-ray/CT/MRI Films', category: 'document', returnable: true },
  { name: 'Insurance Papers', category: 'document', returnable: true },
  { name: 'Aadhar Card', category: 'document', returnable: true },
  { name: 'PAN Card', category: 'document', returnable: true },
  { name: 'Previous Records', category: 'document', returnable: true },
  
  // Valuables
  { name: 'Jewelry', category: 'valuable', returnable: true },
  { name: 'Watch', category: 'valuable', returnable: true },
  { name: 'Wallet', category: 'valuable', returnable: true },
  { name: 'Cash', category: 'valuable', returnable: true },
  { name: 'Keys', category: 'valuable', returnable: true },
  
  // Other
  { name: 'Books', category: 'other', returnable: true },
  { name: 'Newspaper/Magazine', category: 'other', returnable: true },
  { name: 'Flowers', category: 'other', returnable: false },
  { name: 'Toys', category: 'other', returnable: true },
  { name: 'Religious Items', category: 'other', returnable: true }
];

const UNITS = [
  { value: 'piece', label: 'Piece' },
  { value: 'packet', label: 'Packet' },
  { value: 'bottle', label: 'Bottle' },
  { value: 'box', label: 'Box' },
  { value: 'kg', label: 'Kg' },
  { value: 'liter', label: 'Liter' },
  { value: 'pair', label: 'Pair' },
  { value: 'set', label: 'Set' },
  { value: 'file', label: 'File' },
  { value: 'bundle', label: 'Bundle' }
];

export const ItemSelector = ({ items = [], onChange }) => {
  const [itemInput, setItemInput] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [unit, setUnit] = useState('piece');
  const [openCustomItemDialog, setOpenCustomItemDialog] = useState(false);
  const [customItem, setCustomItem] = useState({
    name: '',
    category: 'other',
    quantity: 1,
    unit: 'piece',
    returnable: false,
    description: ''
  });

  const handleAddItem = (itemName, category = 'other', isReturnable = false) => {
    const newItem = {
      name: itemName,
      category: category,
      quantity: quantity,
      unit: unit,
      is_returnable: isReturnable,
      returned: false
    };
    
    onChange([...items, newItem]);
    setItemInput('');
    setQuantity(1);
    setUnit('piece');
  };

  const handleUpdateQuantity = (index, change) => {
    const updatedItems = [...items];
    const newQuantity = (updatedItems[index].quantity || 0) + change;
    
    if (newQuantity > 0) {
      updatedItems[index].quantity = newQuantity;
      onChange(updatedItems);
    } else {
      updatedItems.splice(index, 1);
      onChange(updatedItems);
    }
  };

  const handleUpdateUnit = (index, newUnit) => {
    const updatedItems = [...items];
    updatedItems[index].unit = newUnit;
    onChange(updatedItems);
  };

  const handleToggleReturnable = (index) => {
    const updatedItems = [...items];
    updatedItems[index].is_returnable = !updatedItems[index].is_returnable;
    onChange(updatedItems);
  };

  const handleRemoveItem = (index) => {
    const updatedItems = [...items];
    updatedItems.splice(index, 1);
    onChange(updatedItems);
  };

  const handleAddCustomItem = () => {
    const newItem = {
      ...customItem,
      is_returnable: customItem.returnable
    };
    delete newItem.returnable;
    
    onChange([...items, newItem]);
    setOpenCustomItemDialog(false);
    setCustomItem({
      name: '',
      category: 'other',
      quantity: 1,
      unit: 'piece',
      returnable: false,
      description: ''
    });
  };

  const filteredItems = selectedCategory
    ? COMMON_ITEMS.filter(item => item.category === selectedCategory)
    : COMMON_ITEMS;

  const getCategoryIcon = (category) => {
    const cat = ITEM_CATEGORIES.find(c => c.value === category);
    return cat ? cat.icon : <CategoryIcon />;
  };

  const getCategoryColor = (category) => {
    const cat = ITEM_CATEGORIES.find(c => c.value === category);
    return cat ? cat.color : '#795548';
  };

  return (
    <Box>
      {/* Category Filter */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Filter by Category
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label="All Items"
            variant={!selectedCategory ? "filled" : "outlined"}
            onClick={() => setSelectedCategory('')}
            icon={<InventoryIcon />}
          />
          {ITEM_CATEGORIES.map(category => (
            <Chip
              key={category.value}
              label={category.label}
              variant={selectedCategory === category.value ? "filled" : "outlined"}
              onClick={() => setSelectedCategory(category.value)}
              icon={category.icon}
              sx={{
                borderColor: category.color,
                bgcolor: selectedCategory === category.value ? category.color : 'transparent',
                color: selectedCategory === category.value ? 'white' : category.color
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* Add Item Section */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
        <Typography variant="subtitle2" gutterBottom>
          Add Items
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <Autocomplete
              freeSolo
              options={filteredItems.map(item => ({
                label: item.name,
                category: item.category,
                returnable: item.returnable
              }))}
              value={itemInput}
              onChange={(event, newValue) => {
                if (newValue && typeof newValue === 'object') {
                  handleAddItem(newValue.label, newValue.category, newValue.returnable);
                  setItemInput('');
                }
              }}
              onInputChange={(event, newInputValue) => {
                setItemInput(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select or Type Item Name"
                  fullWidth
                  size="small"
                />
              )}
              groupBy={(option) => {
                if (typeof option === 'string') return 'Custom';
                const category = ITEM_CATEGORIES.find(c => c.value === option.category);
                return category ? category.label : 'Other';
              }}
              renderOption={(props, option) => (
                <li {...props}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {typeof option === 'object' && getCategoryIcon(option.category)}
                    <Typography variant="body2">{typeof option === 'object' ? option.label : option}</Typography>
                  </Stack>
                </li>
              )}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              size="small"
              InputProps={{ inputProps: { min: 1 } }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              select
              fullWidth
              label="Unit"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              size="small"
            >
              {UNITS.map(unitOption => (
                <MenuItem key={unitOption.value} value={unitOption.value}>
                  {unitOption.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={() => {
                  if (itemInput.trim()) {
                    const existingItem = COMMON_ITEMS.find(item => item.name.toLowerCase() === itemInput.toLowerCase());
                    if (existingItem) {
                      handleAddItem(itemInput, existingItem.category, existingItem.returnable);
                    } else {
                      setCustomItem({
                        ...customItem,
                        name: itemInput,
                        quantity: quantity,
                        unit: unit
                      });
                      setOpenCustomItemDialog(true);
                    }
                  }
                }}
                disabled={!itemInput.trim()}
                fullWidth
              >
                Add
              </Button>
              <Button
                variant="outlined"
                onClick={() => setOpenCustomItemDialog(true)}
                title="Add Custom Item"
              >
                +
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {/* Quick Add Chips */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Quick Add:
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {filteredItems.slice(0, 6).map(item => (
              <Chip
                key={item.name}
                label={item.name}
                onClick={() => handleAddItem(item.name, item.category, item.returnable)}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
        </Box>
      </Paper>

      {/* Selected Items List */}
      {items.length > 0 ? (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle1">
              Selected Items ({items.length})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total Items: {items.reduce((sum, item) => sum + (item.quantity || 1), 0)}
            </Typography>
          </Stack>
          
          <Stack spacing={1}>
            {items.map((item, index) => (
              <Paper
                key={index}
                sx={{
                  p: 1.5,
                  borderLeft: `4px solid ${getCategoryColor(item.category)}`,
                  bgcolor: item.is_returnable ? '#fff8e1' : 'white'
                }}
              >
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={12} sm={4}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {getCategoryIcon(item.category)}
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {item.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ITEM_CATEGORIES.find(c => c.value === item.category)?.label || 'Other'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                  
                  <Grid item xs={12} sm={3}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleUpdateQuantity(index, -1)}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      
                      <Typography sx={{ minWidth: '40px', textAlign: 'center' }}>
                        {item.quantity}
                      </Typography>
                      
                      <IconButton
                        size="small"
                        onClick={() => handleUpdateQuantity(index, 1)}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                      
                      <TextField
                        select
                        size="small"
                        value={item.unit || 'piece'}
                        onChange={(e) => handleUpdateUnit(index, e.target.value)}
                        sx={{ width: '80px', ml: 1 }}
                      >
                        {UNITS.map(unitOption => (
                          <MenuItem key={unitOption.value} value={unitOption.value}>
                            {unitOption.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                  </Grid>
                  
                  <Grid item xs={12} sm={3}>
                    <Tooltip title={item.is_returnable ? "Returnable Item" : "Non-returnable Item"}>
                      <Chip
                        label={item.is_returnable ? "Returnable" : "Non-returnable"}
                        size="small"
                        color={item.is_returnable ? "warning" : "default"}
                        variant={item.is_returnable ? "filled" : "outlined"}
                        onClick={() => handleToggleReturnable(index)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </Tooltip>
                  </Grid>
                  
                  <Grid item xs={12} sm={2} sx={{ textAlign: 'right' }}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveItem(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        </Box>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#fafafa' }}>
          <InventoryIcon sx={{ fontSize: 48, color: '#ddd', mb: 2 }} />
          <Typography color="text.secondary">
            No items added yet. Select items from above or add custom items.
          </Typography>
        </Paper>
      )}

      {/* Custom Item Dialog */}
      <Dialog
        open={openCustomItemDialog}
        onClose={() => setOpenCustomItemDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Custom Item</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Item Name"
              value={customItem.name}
              onChange={(e) => setCustomItem({ ...customItem, name: e.target.value })}
              fullWidth
              required
            />
            
            <TextField
              select
              label="Category"
              value={customItem.category}
              onChange={(e) => setCustomItem({ ...customItem, category: e.target.value })}
              fullWidth
            >
              {ITEM_CATEGORIES.map(category => (
                <MenuItem key={category.value} value={category.value}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {category.icon}
                    <span>{category.label}</span>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Quantity"
                  type="number"
                  value={customItem.quantity}
                  onChange={(e) => setCustomItem({ 
                    ...customItem, 
                    quantity: Math.max(1, parseInt(e.target.value) || 1) 
                  })}
                  fullWidth
                  InputProps={{ inputProps: { min: 1 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  label="Unit"
                  value={customItem.unit}
                  onChange={(e) => setCustomItem({ ...customItem, unit: e.target.value })}
                  fullWidth
                >
                  {UNITS.map(unitOption => (
                    <MenuItem key={unitOption.value} value={unitOption.value}>
                      {unitOption.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            
            <TextField
              label="Description"
              value={customItem.description}
              onChange={(e) => setCustomItem({ ...customItem, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
              placeholder="Optional description"
            />
            
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body2">Is this item returnable?</Typography>
              <Button
                variant={customItem.returnable ? "contained" : "outlined"}
                color={customItem.returnable ? "warning" : "default"}
                onClick={() => setCustomItem({ ...customItem, returnable: !customItem.returnable })}
                size="small"
              >
                {customItem.returnable ? "Yes, returnable" : "No, consumable"}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCustomItemDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddCustomItem}
            disabled={!customItem.name.trim()}
          >
            Add Item
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};